import { useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import map from 'lodash/map';
import Text from '../components/atoms/Text';
import { getCategories } from '../data/Concepts';
import type { Configuration } from '../data/Configuration';
import type { UpdateConfigurationArgs } from '../store/ConfigurationSlice';
import type { NewPlaythroughArgs } from '../store/PlaythroughSlice';
import DataList from './DataList';

type Props = StateProps & DispatchProps & OwnProps;

const Overlay = styled.div`
	background: ${(props) => props.theme.colors.veryLightBlue};
	border: 1px solid ${(props) => props.theme.colors.darkBlue};
	color: ${(props) => props.theme.colors.darkText};
	left: 0;
	margin: 2em;
	opacity: 0.95;
	padding: 1em;
	position: absolute;
	top: 0;
`;

const Options = styled.div`
	float: left;
`;

const Note = styled.aside`
	background-color: ${(props) => props.theme.colors.darkBlue};
	border-radius: 1em;
	color: ${(props) => props.theme.colors.lightText};
	padding: 1em;
`;

const Data = styled.div`
	padding: 0 1em;
	max-height: 80vh;
	overflow-y: scroll;
`;

const parseFloatValue = (value: string): number | undefined => {
	const floatValue = parseFloat(value);
	return isNaN(floatValue) ? undefined : floatValue;
};

const parseIntValue = (value: string): number | undefined => {
	const intValue = parseInt(value);
	return isNaN(intValue) ? undefined : intValue;
};

export interface StateProps {
	configuration: Configuration;
}

export interface DispatchProps {
	updateConfiguration: (args: UpdateConfigurationArgs) => void;
	resetConfiguration: () => void;
	newPlaythrough: (args: NewPlaythroughArgs) => void;
	clearHighscore: () => void;
}

export interface OwnProps {
	visible: boolean;
	onClose: () => void;
}

const ConfigurationOverlay: FunctionComponent<Props> = (props: Props) => {
	const {
		configuration,
		visible,
		updateConfiguration,
		resetConfiguration,
		newPlaythrough,
		clearHighscore,
		onClose
	} = props;
	const { t } = useTranslation();
	const [showData, setShowData] = useState(false);
	const categories = useMemo(() => getCategories(), []);

	if (!visible) {
		return <></>;
	}

	return (
		<Overlay>
			<Options>
				<Note>
					<Text content={t('configuration.notes', { returnObjects: true })} />
				</Note>
				<dl>
					<dt key="background_sound_enabled_label">
						<label>
							<Trans>configuration.background_sound_enabled</Trans>
						</label>
					</dt>
					<dd key="background_sound_enabled">
						<input
							type="checkbox"
							checked={configuration.backgroundSoundEnabled}
							onChange={(event) =>
								updateConfiguration({
									backgroundSoundEnabled: event.currentTarget.checked
								})
							}
						/>
					</dd>
					<dt key="background_sound_volume_label">
						<label>
							<Trans>configuration.background_sound_volume</Trans>
						</label>
					</dt>
					<dd key="background_sound_volume">
						<input
							type="range"
							min="0"
							max="1"
							step="0.1"
							value={configuration.backgroundSoundVolume}
							onChange={(event) =>
								updateConfiguration({
									backgroundSoundVolume: parseFloatValue(
										event.currentTarget.value
									)
								})
							}
						/>
					</dd>
					<dt key="sound_effects_enabled_label">
						<label>
							<Trans>configuration.sound_effects_enabled</Trans>
						</label>
					</dt>
					<dd key="sound_effects_enabled">
						<input
							type="checkbox"
							checked={configuration.soundEffectsEnabled}
							onChange={(event) =>
								updateConfiguration({
									soundEffectsEnabled: event.currentTarget.checked
								})
							}
						/>
					</dd>
					<dt key="sound_effects_volume_label">
						<label>
							<Trans>configuration.sound_effects_volume</Trans>
						</label>
					</dt>
					<dd key="sound_effects_volume">
						<input
							type="range"
							min="0"
							max="1"
							step="0.1"
							value={configuration.soundEffectsVolume}
							onChange={(event) =>
								updateConfiguration({
									soundEffectsVolume: parseFloatValue(event.currentTarget.value)
								})
							}
						/>
					</dd>
					<dt key="default_playername_label">
						<label>
							<Trans>configuration.default_playername</Trans>
						</label>
					</dt>
					<dd key="default_playername">
						<input
							type="text"
							size={40}
							value={configuration.defaultPlayerName}
							onChange={(event) =>
								updateConfiguration({
									defaultPlayerName: event.currentTarget.value
								})
							}
						/>
					</dd>
					<dt key="reset_playthrough_after_intro_label">
						<label>
							<Trans>configuration.reset_playthrough_after_intro</Trans>
						</label>
					</dt>
					<dd key="reset_playthrough_after_intro">
						<input
							type="checkbox"
							checked={configuration.resetPlaythroughAfterIntro}
							onChange={(event) =>
								updateConfiguration({
									resetPlaythroughAfterIntro: event.currentTarget.checked
								})
							}
						/>
					</dd>
					<dt key="round_count_per_playthrough_label">
						<label>
							<Trans>configuration.round_count_per_playthrough</Trans>
						</label>
					</dt>
					<dd key="round_count_per_playthrough">
						<input
							type="number"
							size={10}
							value={configuration.roundCountPerPlaythrough}
							onChange={(event) =>
								updateConfiguration({
									roundCountPerPlaythrough: parseIntValue(
										event.currentTarget.value
									)
								})
							}
						/>
					</dd>
					<dt key="initial_persona_count_per_playthrough_label">
						<label>
							<Trans>configuration.initial_persona_count_per_playthrough</Trans>
						</label>
					</dt>
					<dd key="initial_persona_count_per_playthrough">
						<input
							type="number"
							size={10}
							value={configuration.initialPersonaCountPerPlaythrough}
							onChange={(event) =>
								updateConfiguration({
									initialPersonaCountPerPlaythrough: parseIntValue(
										event.currentTarget.value
									)
								})
							}
						/>
					</dd>
					<dt key="random_initial_personas_label">
						<label>
							<Trans>configuration.random_initial_personas</Trans>
						</label>
					</dt>
					<dd key="random_initial_personas">
						<input
							type="checkbox"
							checked={configuration.randomInitialPersonas}
							onChange={(event) =>
								updateConfiguration({
									randomInitialPersonas: event.currentTarget.checked
								})
							}
						/>
					</dd>
					<dt key="additional_persona_count_per_round_label">
						<label>
							<Trans>configuration.additional_persona_count_per_round</Trans>
						</label>
					</dt>
					<dd key="additional_persona_count_per_round">
						<input
							type="number"
							size={10}
							value={configuration.additionalPersonaCountPerRound}
							onChange={(event) =>
								updateConfiguration({
									additionalPersonaCountPerRound: parseIntValue(
										event.currentTarget.value
									)
								})
							}
						/>
					</dd>
					<dt key="random_additional_personas_label">
						<label>
							<Trans>configuration.random_additional_personas</Trans>
						</label>
					</dt>
					<dd key="random_additional_personas">
						<input
							type="checkbox"
							checked={configuration.randomAdditionalPersonas}
							onChange={(event) =>
								updateConfiguration({
									randomAdditionalPersonas: event.currentTarget.checked
								})
							}
						/>
					</dd>
					<dt key="global_highscore_enabled_label">
						<label>
							<Trans>configuration.global_highscore_enabled</Trans>
						</label>
					</dt>
					<dd key="global_highscore_enabled">
						<input
							type="checkbox"
							checked={configuration.globalHighscoreEnabled}
							onChange={(event) =>
								updateConfiguration({
									globalHighscoreEnabled: event.currentTarget.checked
								})
							}
						/>
					</dd>
					<dt key="max_highscore_entry_count_label">
						<label>
							<Trans>configuration.max_highscore_entry_count</Trans>
						</label>
					</dt>
					<dd key="max_highscore_entry_count">
						<input
							type="number"
							size={10}
							value={configuration.maxHighscoreEntryCount}
							onChange={(event) =>
								updateConfiguration({
									maxHighscoreEntryCount: parseIntValue(
										event.currentTarget.value
									)
								})
							}
						/>
					</dd>
					<dt key="backend_url_label">
						<label>
							<Trans>configuration.backend_url</Trans>
						</label>
					</dt>
					<dd key="backend_url">
						<input
							type="text"
							size={60}
							value={configuration.backendUrl}
							onChange={(event) =>
								updateConfiguration({ backendUrl: event.currentTarget.value })
							}
						/>
					</dd>
					<dt key="survey_url_label">
						<label>
							<Trans>configuration.survey_url</Trans>
						</label>
					</dt>
					<dd key="survey_url">
						<input
							type="text"
							size={60}
							value={configuration.surveyUrl}
							onChange={(event) =>
								updateConfiguration({ surveyUrl: event.currentTarget.value })
							}
						/>
					</dd>
					<dt key="default_category_label">
						<label>
							<Trans>configuration.default_category</Trans>
						</label>
					</dt>
					<dd key="default_category">
						<select
							value={configuration.defaultCategory}
							onChange={(event) =>
								updateConfiguration({
									defaultCategory: event.currentTarget.value
								})
							}
						>
							{map(categories, (category) => (
								<option key={category} value={category}>
									{category}
								</option>
							))}
						</select>
					</dd>
				</dl>

				<button key="reset_configuration" onClick={() => resetConfiguration()}>
					<Trans>configuration.reset_configuration</Trans>
				</button>
				<button key="new_playthrough" onClick={() => newPlaythrough({})}>
					<Trans>configuration.apply_configuration</Trans>
				</button>
				<button key="clear_highscore" onClick={() => clearHighscore()}>
					<Trans>configuration.clear_highscore</Trans>
				</button>
				<button key="show_data" onClick={() => setShowData(!showData)}>
					<Trans>
						{showData ? 'configuration.hide_data' : 'configuration.show_data'}
					</Trans>
				</button>
				<button key="close" onClick={onClose}>
					<Trans>configuration.close</Trans>
				</button>
			</Options>
			{showData && (
				<Data>
					<DataList />
				</Data>
			)}
		</Overlay>
	);
};

export default ConfigurationOverlay;
